// src/components/NewDonationComponentForm.js
import React, { useState, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebaseConfig';
import { DONATION_TOKENS, resolveDonationToken } from '../utils/donationTokens';

export default function NewDonationComponentForm({
                                                     userId,
                                                     // If editing, you can pass initial fields:
                                                     // initial = { campaignName, description, imageUrl, goalAmount, currency }
                                                     initial = null,

                                                     // Save-only callback (parent saves to state)
                                                     // onSubmit: (payload) => void
                                                     onSubmit,

                                                     // NEW: Save + Publish callback (parent saves, then publishes)
                                                     // onSubmitAndPublish: (payload) => void
                                                     enablePublishButton = false,
                                                     onSubmitAndPublish,

                                                     onCancel,
                                                     error,
                                                 }) {
    const [campaignName, setCampaignName] = useState(initial?.campaignName ?? '');
    const [description, setDescription]   = useState(initial?.description ?? '');
    const [goalAmount, setGoalAmount]     = useState(
        typeof initial?.goalAmount === 'number' ? String(initial.goalAmount) : (initial?.goalAmount ?? '')
    );
    const [currency, setCurrency]         = useState(initial?.currency ?? 'SOL');

    const [file, setFile]                 = useState(null); // optional feature image
    const [uploading, setUploading]       = useState(false);
    const [localError, setLocalError]     = useState('');

    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

    useEffect(() => {
        setLocalError('');
    }, [campaignName, description, goalAmount, currency, file]);

    const handleImageChange = (e) => {
        const f = e.target.files?.[0] ?? null;
        setLocalError('');
        if (!f) {
            setFile(null);
            return;
        }
        if (f.size > MAX_IMAGE_SIZE) {
            const msg = `Image is too large. Maximum size is ${MAX_IMAGE_SIZE / (1024 * 1024)} MB.`;
            alert(msg);
            setLocalError(msg);
            return;
        }
        setFile(f);
    };

    /**
     * Upload the optional feature image if selected, otherwise reuse initial.imageUrl (if any).
     * Resolves a string imageUrl.
     */
    const uploadFeatureImageIfNeeded = () =>
        new Promise(async (resolve, reject) => {
            try {
                // No new file: reuse existing imageUrl or empty
                if (!file) {
                    return resolve(initial?.imageUrl ?? '');
                }

                if (file.size > MAX_IMAGE_SIZE) {
                    const msg = `Image is too large. Maximum size is ${MAX_IMAGE_SIZE / (1024 * 1024)} MB.`;
                    alert(msg);
                    setLocalError(msg);
                    return reject(new Error('image-too-large'));
                }

                setUploading(true);
                const uid = userId || auth?.currentUser?.uid || 'anonymous';
                const path = `images/${uid}/${Date.now()}_${file.name}`;
                const storageRef = ref(storage, path);
                const task = uploadBytesResumable(storageRef, file);

                task.on(
                    'state_changed',
                    () => {},
                    (err) => {
                        console.error(err);
                        setUploading(false);
                        setLocalError('Image upload failed.');
                        reject(err);
                    },
                    async () => {
                        try {
                            const url = await getDownloadURL(task.snapshot.ref);
                            setUploading(false);
                            resolve(url);
                        } catch (e) {
                            console.error(e);
                            setUploading(false);
                            setLocalError('Unable to retrieve image URL.');
                            reject(e);
                        }
                    }
                );
            } catch (e) {
                console.error(e);
                setUploading(false);
                setLocalError('Upload failed.');
                reject(e);
            }
        });

    const buildPayload = (imageUrl) => {
        // Normalize goalAmount -> number if valid, else empty string stays as-is
        const parsed = goalAmount === '' ? '' : Number(goalAmount);
        const finalGoal = Number.isFinite(parsed) ? parsed : '';
        const token = resolveDonationToken(currency || 'SOL');

        return {
            campaignName: campaignName.trim(),
            description: description?.trim() ?? '',
            imageUrl: imageUrl ?? '',
            goalAmount: finalGoal,
            currency: currency || 'SOL',
            tokenMint: token?.mint || '',
        };
    };

    const handleSave = async () => {
        try {
            if (!campaignName.trim()) {
                setLocalError('Campaign name is required.');
                return;
            }
            const imageUrl = await uploadFeatureImageIfNeeded();
            onSubmit?.(buildPayload(imageUrl));
        } catch {
            // localError/alerts already set
        }
    };

    const handleSaveAndPublish = async () => {
        try {
            if (!campaignName.trim()) {
                setLocalError('Campaign name is required.');
                return;
            }
            const imageUrl = await uploadFeatureImageIfNeeded();
            onSubmitAndPublish?.(buildPayload(imageUrl));
        } catch {
            // localError/alerts already set
        }
    };

    return (
        <div className="new-component-form donation-form">
            <h1>Donation Component</h1>

            <div style={{ marginBottom: 12 }}>
                <label>Campaign Name</label>
                <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Enter a campaign name"
                    className="muulti-input"
                />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label>Description</label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter campaign description"
                    rows={3}
                    className="muulti-textarea"
                />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label>Feature Image (optional)</label>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="muulti-input"
                />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label>Goal Amount (optional)</label>
                <input
                    type="number"
                    step="any"
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(e.target.value)}
                    placeholder="e.g. 5"
                    className="muulti-input"
                />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label>Currency</label>
                <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="muulti-input"
                >
                    {DONATION_TOKENS.map((token) => (
                        <option key={token.symbol} value={token.symbol}>
                            {token.symbol}
                        </option>
                    ))}
                </select>
            </div>

            {(localError || error) && (
                <p className="error" style={{ marginBottom: 12 }}>
                    {localError || error}
                </p>
            )}

            <div className="muulti-container-button-container">
                <button
                    onClick={handleSave}
                    disabled={uploading || !campaignName.trim()}
                    className="muulti-button"
                >
                    {uploading ? 'Uploading…' : 'Save Panel'}
                </button>

                {enablePublishButton && (
                    <button
                        onClick={handleSaveAndPublish}
                        disabled={uploading || !campaignName.trim()}
                        className="muulti-button"
                    >
                        {uploading ? 'Uploading…' : 'Save Panel & Publish'}
                    </button>
                )}

                <button onClick={onCancel} className="muulti-button">Cancel</button>
            </div>
        </div>
    );
}
